/**
 * Expandable container that clamps arbitrary children to a max height
 * with a gradient fade and expand/collapse toggle. Content that fits
 * naturally renders without any chrome.
 */
import { ChevronDown, ChevronUp } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

interface ExpandableContentProps {
  /** Content to render — works with any React nodes, not just text. */
  children: ReactNode
  /** Max collapsed height in pixels. Defaults to 150. */
  maxHeight?: number
  /** Extra pixels beyond `maxHeight` content can exceed before clamping kicks in. Prevents a toggle for content that's only marginally taller than `maxHeight`. Defaults to 30. */
  threshold?: number
  /** Rendered after the content, aligned opposite the expand toggle. */
  footer?: ReactNode

}

export function ExpandableContent({
  children,
  maxHeight = 120,
  threshold = 40,
  footer,
}: ExpandableContentProps) {
  const innerRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const measure = useCallback(() => {
    const el = innerRef.current
    if (!el) return
    // Only clamp if content exceeds maxHeight + grace, avoiding a toggle
    // for content that's only marginally taller than the threshold
    setIsOverflowing(el.offsetHeight > maxHeight + threshold)
  }, [maxHeight, threshold])

  useEffect(() => {
    measure()

    const el = innerRef.current
    if (!el) return

    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [measure])

  const showToggle = isOverflowing && !expanded

  return (
    <div>
      <div
        className="relative overflow-hidden"
        style={{
          maxHeight: expanded || !isOverflowing ? undefined : maxHeight,
          ...(showToggle ? { maskImage: 'linear-gradient(to bottom, black 50%, transparent)' } : undefined),
        }}
      >
        <div ref={innerRef}>{children}</div>
      </div>
      {(isOverflowing || footer) && (
        <div className="mt-1 flex items-center justify-between gap-6">
          {isOverflowing && (
            <button
              type="button"
              className="inline-flex h-6 shrink-0 cursor-pointer items-center gap-1 rounded-sm text-xs font-semibold text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
            >
              {expanded ? (
                <>
                  <ChevronUp className="size-3" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="size-3" />
                  Show more
                </>
              )}
            </button>
          )}
          {footer && <div className="ml-auto">{footer}</div>}
        </div>
      )}
    </div>
  )
}
